import React, { useEffect, useState, useCallback } from 'react';
import styled from 'styled-components';

// types
import { Colors, Typography } from '@inplayer-org/inplayer-ui';
import { CountryPerMethod } from 'ducks/merchantDucks/payments/types';

// data
import countries from 'data/countries';
import { paymentMethods } from 'data/paymentMethods';

// utils
import { loadCountryFlags, ImportReturnType } from 'utils/loadCountryFlags';
import formatNumber from 'utils/formatNumber';

interface Props {
  isTopCountriesView: boolean;
  paymentDetailsPerCountry: CountryPerMethod;
  currentCountry: string;
  setCurrentCountryForPaymentMethods: (currentCountry: string) => void;
  totalPayments: number;
}
const Container = styled.div`
  width: 80%;
  overflow-y: auto;
  height: 400px;
`;

const Wrapper = styled.div`
  display: flex;
  justify-content: space-between;
  border-bottom: 1px solid ${Colors.gray};
  border-right: 1px solid ${Colors.gray};
  padding: 0.5rem 0;
`;

export const Icon = styled.i<{ imageUrl: string }>`
  background-image: url(${({ imageUrl }) => imageUrl});
  height: 20px;
  width: 20%;
  margin-right: 0.5rem;
  top: 5px;
  position: relative;
  background-repeat: no-repeat;
`;

const Flex = styled.div`
  display: flex;
  justify-content: space-between;
  width: 100%;
`;

const StyledTypography = styled(Typography)<{ color?: string; isBold?: boolean; width?: string; isPointer?: boolean }>`
  color: ${({ color }) => color || Colors.fontGray};
  font-weight: ${({ isBold, theme }) => isBold && theme.font.weights.bold};
  cursor: ${({ isPointer }) => isPointer && 'pointer'};
  margin: 0 0.5rem;
  width: ${({ width }) => width};

  :hover {
    color: ${({ color }) => color || Colors.fontDarkGray};
  }
`;

const TopCountiesGeneralComponent: React.FC<Props> = ({
  paymentDetailsPerCountry,
  isTopCountriesView,
  currentCountry,
  setCurrentCountryForPaymentMethods,
  totalPayments,
}) => {
  const [flagsSrc, setFlagsSrc] = useState<ImportReturnType[]>([]);
  const loadFlags = useCallback(async () => {
    const uppercaseCountries = Object.keys(paymentDetailsPerCountry).map((country) => country.toUpperCase());
    const flags = await loadCountryFlags(uppercaseCountries);
    setFlagsSrc(flags);
  }, [paymentDetailsPerCountry]);

  useEffect(() => {
    loadFlags();
  }, [paymentDetailsPerCountry]);

  const topCountriesView =
    paymentDetailsPerCountry &&
    Object.keys(paymentDetailsPerCountry).map((country, index) => (
      <Wrapper
        key={index}
        onClick={country !== 'Others' ? () => setCurrentCountryForPaymentMethods(country) : undefined}
      >
        <Flex>
          <Icon imageUrl={flagsSrc[index]?.default} />
          <StyledTypography variant="p" width="80%" isPointer={country !== 'Others'}>
            {country === 'Others'
              ? 'Others'
              : countries.find((countryObject) => countryObject.code === country.toUpperCase())?.name ?? 'N/A'}
          </StyledTypography>
        </Flex>

        <StyledTypography isBold color={Colors.blue} variant="p">
          {formatNumber(paymentDetailsPerCountry[country].total)}
        </StyledTypography>
      </Wrapper>
    ));

  const currentCountryObject = paymentDetailsPerCountry[currentCountry];
  const percentOfPayments = (currentCountryObject?.total / totalPayments) * 100;
  const currentCountryView = (
    <>
      {currentCountryObject &&
        currentCountryObject?.per_method &&
        Object.keys(currentCountryObject?.per_method).map((paymentMethod: any, index: string | number | undefined) => (
          <Wrapper key={index}>
            <Flex>
              <Icon imageUrl={paymentMethods[paymentMethod]} />
              <StyledTypography variant="p" width="80%">
                {paymentMethod}
              </StyledTypography>
            </Flex>

            <StyledTypography isBold color={Colors.blue} variant="p">
              {formatNumber(currentCountryObject.per_method[paymentMethod].total)}
            </StyledTypography>
          </Wrapper>
        ))}
      <StyledTypography isBold variant="p">
        Number of payments: {currentCountryObject ? formatNumber(currentCountryObject.total) : 'N/A'}
      </StyledTypography>
      <StyledTypography isBold variant="p">
        {percentOfPayments < 0.01 ? 'less than 0.01' : percentOfPayments.toFixed(2)}% of total payments
      </StyledTypography>
    </>
  );

  return <Container>{isTopCountriesView ? topCountriesView : currentCountryView}</Container>;
};

export default TopCountiesGeneralComponent;
